import { getWindowOrThrow } from "@faire/web/common/globals/getWindow";
import { singletonGetter } from "@faire/web/common/singletons/getter";
import type { History } from "history";

import { getRelativeEventTimestamp } from "./getRelativeEventTimestamp";

export class HistoryTiming {
  static get = singletonGetter(HistoryTiming);

  /**
   * Legacy _timeOfLastRouteChange which updates after every
   * React Router navigation.
   */
  private _timeOfLastRouteChange: number = 0;

  /**
   * Keeps track of last route change, and ignores new updates if
   * the URL hasn't changed. This allows multiple timing sources
   * to be used (ie. link onClick, popstate event, and React Router)
   * without the later events incorrectly updating the timestamp,
   * which would make UPLT latency appear lower than it really was.
   */
  private _lastRouteChangeInfo: {
    readonly timeStamp: DOMHighResTimeStamp;
    readonly pathname: string;
    readonly search: string;
  } = {
    timeStamp: 0,
    pathname: "",
    search: "",
  };

  initialize = (history: History) => {
    history.listen((newLocation) => {
      const now = getWindowOrThrow().performance.now();
      this._timeOfLastRouteChange = Math.round(now);

      this.updateTimeOfLastRouteChange(newLocation, now, "react-router");
    });
  };

  get timeOfLastRouteChange() {
    return this._timeOfLastRouteChange;
  }

  get isInitialLoad() {
    return this._timeOfLastRouteChange === 0;
  }

  get timeOfLastRouteChangeV2() {
    return this._lastRouteChangeInfo.timeStamp;
  }

  updateTimeOfLastRouteChange = (
    newLocation: { pathname: string; search: string },
    timeStampMaybeAbsolute: DOMHighResTimeStamp,
    _source: "popstate" | "react-router"
  ) => {
    const timeStamp = getRelativeEventTimestamp(timeStampMaybeAbsolute);
    if (timeStamp == null) {
      return;
    }

    if (!isLocationEqual(newLocation, this._lastRouteChangeInfo)) {
      this._lastRouteChangeInfo = {
        timeStamp,
        pathname: newLocation.pathname,
        search: newLocation.search,
      };
    }
  };
}

function isLocationEqual(
  a: { pathname: string; search: string },
  b: { pathname: string; search: string }
) {
  return a.pathname === b.pathname && a.search === b.search;
}
